import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Share2, Calendar } from 'lucide-react';
import { fetchArticleBySlug, fetchProgramBySlug } from '@/services/strapi';
import type { NewsItem } from '@/constants/news';
import { maybeTransliterate } from '@/utils/transliterate';

interface ArticleDetailScreenProps {
  /** 'article' (default) — fetcha iz Articles kolekcije; 'program' — fetcha iz Programs */
  type?: 'article' | 'program';
}

export default function ArticleDetailScreen({ type = 'article' }: ArticleDetailScreenProps) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'bs';

  const [article, setArticle] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!slug) {
      setError(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);

    // TASK 31: /program/:slug → primarno Programs kolekcija; /article/:slug i
    // /news/:slug → Articles (sa legacy fallback chain-om za news-items + programs).
    const fetcher = type === 'program' ? fetchProgramBySlug : fetchArticleBySlug;
    fetcher(slug, locale)
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setError(true);
        } else {
          setArticle(data);
          window.scrollTo({ top: 0, behavior: 'auto' });
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, locale, type]);

  async function handleShare() {
    if (!article) return;
    const url = window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({
          title: article.title,
          text: article.excerpt || article.description,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        window.alert(t('news.linkCopied'));
      }
    } catch (e) {
      console.warn('[share] error:', e);
    }
  }

  const dateLocale =
    locale === 'sr-Cyrl' ? 'sr-Cyrl-RS' : locale === 'sr' ? 'sr-Latn-RS' : locale;

  const formattedDate = article?.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString(dateLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse text-text-muted">{t('news.loading')}</div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6 text-center">
        <h1 className="text-2xl font-bold mb-4 text-gray-900">
          {t('news.notFound')}
        </h1>
        <button
          onClick={() => navigate('/')}
          className="text-accent-pink hover:underline"
        >
          {t('news.backToList')}
        </button>
      </div>
    );
  }

  const displayTitle = maybeTransliterate(article.title, locale);
  const displayExcerpt = maybeTransliterate(article.excerpt || article.description, locale);

  return (
    <article className="min-h-screen bg-gray-50">
      <div className="relative h-64 sm:h-96 overflow-hidden">
        <img
          src={article.image}
          alt={displayTitle}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        <button
          onClick={() => navigate(-1)}
          className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition"
        >
          <ArrowLeft size={20} />
          <span>{t('news.back')}</span>
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-12">
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100">
            {formattedDate ? (
              <div className="flex items-center gap-2 text-text-muted text-sm">
                <Calendar size={16} />
                <span>{formattedDate}</span>
              </div>
            ) : (
              <div />
            )}
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition text-sm font-medium text-gray-700"
            >
              <Share2 size={16} />
              <span>{t('news.share')}</span>
            </button>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            {displayTitle}
          </h1>

          {displayExcerpt && (
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              {displayExcerpt}
            </p>
          )}

          <div className="article-body prose prose-lg max-w-none">
            {article.content && article.content.trim() ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {maybeTransliterate(article.content, locale) || ''}
              </ReactMarkdown>
            ) : (
              <p className="text-text-muted italic">
                {t('news.contentComingSoon')}
              </p>
            )}
          </div>
        </div>

        <div className="mt-8 mb-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-accent-pink hover:gap-3 transition-all"
          >
            <ArrowLeft size={16} />
            <span>{t('news.backToList')}</span>
          </Link>
        </div>
      </div>
    </article>
  );
}
